// src/components/layout/AppShell.tsx
import React, { useEffect, useState } from 'react';

export default function AppShell({
    header,
    sidebar,
    children,
    defaultCollapsed = true,
}: {
    header: React.ReactNode;
    sidebar?: React.ReactNode;
    children: React.ReactNode;
    defaultCollapsed?: boolean;
}) {
    const KEY = 'app:sidebar:collapsed';
    const [collapsed, setCollapsed] = useState<boolean>(() => {
        try {
            const raw = localStorage.getItem(KEY);
            return raw == null ? defaultCollapsed : JSON.parse(raw);
        } catch {
            return defaultCollapsed;
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem(KEY, JSON.stringify(collapsed));
        } catch {}
    }, [collapsed]);

    // 모바일 드로어 상태(기존 유지)
    const [drawerOpen, setDrawerOpen] = useState(false);
    useEffect(() => {
        const h = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setDrawerOpen(false);
                setCollapsed(true);
            }
        };
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
    }, []);

    return (
        <div className="w-full min-h-screen bg-gray-50">
            {/* Header */}
            <header className="sticky top-0 z-40 border-b bg-white">
                {React.isValidElement(header)
                    ? React.cloneElement(header as any, {
                          onToggleSidebar: () => {
                              if (window.innerWidth < 768) {
                                  setDrawerOpen(true);
                              } else {
                                  setCollapsed((c) => !c);
                              }
                          },
                          sidebarCollapsed: collapsed,
                      })
                    : header}
            </header>

            {/* Body */}
            <div className="w-full //mx-auto //max-w-[1400px] relative">
                <div className="flex">
                    {/* 데스크탑: 콘텐츠를 밀지 않고, 반투명 패널이 위로 겹침 */}
                    {/* 클릭-투-클로즈 투명 오버레이 (배경 흐림/딤 없음) */}
                    {sidebar && !collapsed && (
                        <button
                            type="button"
                            aria-label="Close sidebar overlay"
                            className="hidden md:block fixed inset-0 z-40 bg-transparent"
                            onClick={() => setCollapsed(true)}
                        />
                    )}

                    {/* 플로팅 사이드바 패널 (반투명) */}
                    {sidebar && (
                        <div
                            className={`
                hidden md:block fixed left-0 top-0 z-50 h-full w-56
                border-r shadow-lg
                bg-white/100  /* ← 패널 자체 반투명 */
                backdrop-saturate-100  /* 색 왜곡 최소화 */
                transition-transform duration-200 ease-out
                ${collapsed ? '-translate-x-full pointer-events-none' : 'translate-x-0'}
              `}
                            aria-label="Primary navigation"
                            aria-expanded={!collapsed}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {React.isValidElement(sidebar)
                                ? React.cloneElement(sidebar as any, { collapsed: false })
                                : sidebar}
                        </div>
                    )}

                    {/* 모바일 드로어 (기존 유지) */}
                    {sidebar && (
                        <div
                            className={`fixed inset-0 z-50 md:hidden ${drawerOpen ? 'block' : 'hidden'}`}
                            role="dialog"
                            aria-modal="true"
                            onClick={() => setDrawerOpen(false)}
                        >
                            {/* 모바일은 살짝 딤을 유지 (원래대로) */}
                            <div className="absolute inset-0 bg-black/40" />
                            <div
                                className="absolute left-0 top-0 h-full w-64 bg-white border-r shadow transition-transform"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="p-2 border-b flex items-center justify-between">
                                    <div className="text-sm font-medium">Navigation</div>
                                    <button
                                        className="rounded px-2 py-1 text-sm hover:bg-gray-50"
                                        onClick={() => setDrawerOpen(false)}
                                    >
                                        닫기
                                    </button>
                                </div>
                                {React.isValidElement(sidebar)
                                    ? React.cloneElement(sidebar as any, {
                                          collapsed: false,
                                          onNavigate: () => setDrawerOpen(false),
                                      })
                                    : sidebar}
                            </div>
                        </div>
                    )}

                    {/* Main: 항상 전체 폭 사용 (사이드바는 위로 겹치므로 밀리지 않음) */}
                    <main className="flex-1 min-w-0 p-4 md:p-6">{children}</main>
                </div>
            </div>
        </div>
    );
}
